/** Max length of a stored file title (BE `AbstractFile.title` is CharField(max_length=255)). */
export const FILE_TITLE_MAX_LENGTH = 255;

/** Characters not allowed in a file name (Windows-forbidden set, also unsafe for downloads). */
export const FORBIDDEN_FILE_NAME_CHARS = ['/', '\\', ':', '*', '?', '"', '<', '>', '|'];

// eslint-disable-next-line no-control-regex
const FORBIDDEN_FILE_NAME_REGEX = /[/\\:*?"<>|\u0000-\u001f]/g;

export interface NamedFile {
  file: string;
  title?: string;
}

/** Returns the file name (basename) of a stored file URL, without query string. */
export const getStoredFileName = (fileUrl: string | null | undefined): string => {
  if (!fileUrl) {
    return '';
  }
  const basename = fileUrl.split('?')[0].split('#')[0].split('/').pop() || '';
  try {
    return decodeURIComponent(basename);
  } catch {
    return basename;
  }
};

/** Returns extension including the dot (".pdf"), or empty string. Leading-dot names (".env") have no extension. */
export const getFileExtension = (fileName: string | null | undefined): string => {
  if (!fileName) {
    return '';
  }
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
    return '';
  }
  return fileName.substring(dotIndex);
};

/** Extension of the stored file - it is the authoritative one and the user cannot change it. */
export const getNamedFileExtension = (namedFile: NamedFile): string =>
  getFileExtension(getStoredFileName(namedFile.file)) || getFileExtension(namedFile.title);

/** Editable part of the title - title without the (stored file) extension. */
export const getFileBaseName = (namedFile: NamedFile): string => {
  const extension = getNamedFileExtension(namedFile);
  const title = namedFile.title || getStoredFileName(namedFile.file);
  if (extension && title.toLowerCase().endsWith(extension.toLowerCase())) {
    return title.substring(0, title.length - extension.length);
  }
  return title;
};

/** Builds a full title from the edited base name, always keeping the original extension. */
export const buildFileTitle = (baseName: string, extension: string): string => `${baseName.trim()}${extension}`;

/** Display name of a file - title, or stored file name for files without title. */
export const getFileDisplayName = (namedFile: NamedFile): string => namedFile.title || getStoredFileName(namedFile.file);

/** File name used when downloading - display name with forbidden characters replaced and extension ensured. */
export const getFileDownloadName = (namedFile: NamedFile): string => {
  const extension = getNamedFileExtension(namedFile);
  const baseName = getFileBaseName(namedFile).replace(FORBIDDEN_FILE_NAME_REGEX, '_').trim() || 'download';
  return `${baseName}${extension}`;
};

export const containsForbiddenFileNameChars = (value: string): boolean => new RegExp(FORBIDDEN_FILE_NAME_REGEX.source).test(value);

/**
 * Downloads a file and saves it under the given name. Cross-origin URLs ignore the `download` attribute,
 * so the file is fetched as a Blob first. If fetching fails (e.g. CORS), the file is opened in a new tab.
 */
export const downloadFileAs = async (fileUrl: string, fileName: string): Promise<void> => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
  } catch {
    window.open(fileUrl, '_blank');
  }
};
